// ═══════════════════════════════════════════════════════════
//  FemCare — BookingFlow.jsx
//  Screens: Type → Recommend → Doctor → Mode → Date/Slot →
//           Confirm → Payment → Confirmed
//  All screens driven by dynamic JSON config per type
//  CSS: BookingFlow.module.css
// ═══════════════════════════════════════════════════════════

import React, { useMemo, useState } from "react";
import styles from "../styles/Bookingflow.module.css";
import {
  APPOINTMENT_TYPES,
  CONSULTATION_MODES,
  PAYMENT_METHODS,
  TYPE_CONFIGS,
  ALL_SLOTS,
  MANUALLY_DISABLED_SLOTS,
} from "../config/bookingflow";
import DigitalHealthFAB from "../components/Digitalhealth";

// ─────────────────────────────────────────────────────────
//  SVG Icons
// ─────────────────────────────────────────────────────────
const ArrowRight = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
  </svg>
);

const ArrowLeft = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
  </svg>
);

const ChevDown = () => (
  <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
);

// ─────────────────────────────────────────────────────────
//  Shared: Logo
// ─────────────────────────────────────────────────────────
const Logo = () => (
  <div className='flex items-center gap-2'>
    <img
      src="/Home/logo.svg"
      alt="mukta womens clinic"
      className='h-8 md:h-8 w-auto'
    />
  </div>
);

// ─────────────────────────────────────────────────────────
//  Shared: Header
//  No back button — only Save & Exit (→ "/")
// ─────────────────────────────────────────────────────────
function Header({ step, total }) {
  const pct = Math.round((step / total) * 100);

  return (
    <>
      <div className={styles.header}>
        <Logo />
        <a href="/" className={styles.saveExit}>
          Save &amp; Exit
        </a>
      </div>
      <div className={styles.progressRow}>
        <span className={styles.stepLabel}>
          Step {step} of {total}
        </span>
        <div className={styles.progressTrack}>
          <div className={styles.progressFill} style={{ width: `${pct}%` }} />
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
//  Shared: Radio Dot
// ─────────────────────────────────────────────────────────
function Radio({ checked }) {
  return (
    <div
      className={styles.radio}
      style={
        checked
          ? { background: "var(--plum-d)", borderColor: "var(--plum-d)" }
          : {}
      }
    >
      {checked && <div className={styles.radioDot} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  Shared: Nav Row
// ─────────────────────────────────────────────────────────
function NavRow({ onBack, onNext, nextLabel = "Continue", disabled = false }) {
  return (
    <div className={`${styles.navRow} ${onBack ? styles.navRowSpaced : ""}`}>
      {onBack && (
        <button className={styles.btnOutline} onClick={onBack}>
          <ArrowLeft /> Back
        </button>
      )}
      <button className={styles.btnPrimary} onClick={onNext} disabled={disabled}>
        {nextLabel} <ArrowRight />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  SCREEN 1 — Appointment Type
// ─────────────────────────────────────────────────────────
function Screen1({ onNext, booking, setBooking }) {
  const [sel, setSel] = useState(booking.type || APPOINTMENT_TYPES[0].id);

  function handleNext() {
    setBooking((b) => ({ ...b, type: sel }));
    onNext();
  }

  // Illustration emoji per type
  const illoMap = { doctor: "👩‍⚕️", pregnancy: "🤰", scan: "🔬", pcos: "🩺" };

  return (
    <>
      <Header step={1} total={7} />
      <div className={styles.body}>
          <div>
            <h1 className={styles.screenTitle}>
              What type of appointment do you need?
            </h1>
            <p className={styles.screenSub}>Select the reason for your visit.</p>

          </div>
          <div className={styles.twoCol}>
            <div>
            {APPOINTMENT_TYPES.map((t) => (
              <div
                key={t.id}
                className={`${styles.optionCard} ${sel === t.id ? styles.selected : ""}`}
                onClick={() => setSel(t.id)}
              >
                <div className={styles.optionIcon}>{t.icon}</div>
                <div className={styles.optionText}>
                  <strong>{t.title}</strong>
                  <span>{t.sub}</span>
                </div>
                <Radio checked={sel === t.id} />
              </div>
            ))}
          </div>
          <div className={styles.illoWrap}>
            {/* <div className={styles.illoBg}>{illoMap[sel] || "👩‍⚕️"}</div> */}
            <img src="/images/appointment.svg" className="h-84" />
            {/* <div >
              </div> */}
          </div>
        </div>

        <NavRow onNext={handleNext} nextLabel="Continue" />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
//  SCREEN 2 — Recommendation (driven by booking.type)
// ─────────────────────────────────────────────────────────
function Screen2({ onNext, onBack, booking }) {
  const cfg = TYPE_CONFIGS[booking.type] || TYPE_CONFIGS.doctor;
  const rec = cfg.recommendation;

  return (
    <>
      <Header step={2} total={7} />
      <div className={styles.body}>
        <div>
          <h2 className={styles.screenTitle}>Recommended for you</h2>
          <p className={styles.screenSub}>
            Based on your selection, we recommend these services.
          </p>

        </div>
        <div className={styles.twoCol}>
          <div className={styles.recCard}>
            <div className={styles.recCardHeader}>
              <div className={styles.recCardIcon}>{rec.icon}</div>
              <div>
                <div className={styles.recCardTitle}>{rec.title}</div>
                <div className={styles.recBadge}>{rec.badge}</div>
              </div>
            </div>
            <div className={styles.recCardBody}>
              <div className={styles.recLabel}>Reason summary</div>
              <p className={styles.recReason}>{rec.reasonSummary}</p>
              <div className={styles.recLabel}>Includes</div>
              <div className={styles.recIncludes}>
                {rec.includes.map((item) => (
                  <div key={item} className={styles.recIncludeItem}>
                    <span className={styles.recCheck}>✓</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.sidePanel}>
            <div className={styles.sidePanelTitle}>Your Context</div>
            {Object.entries(rec.context).map(([k, v]) => (
              <div key={k} className={styles.ctxRow}>
                <div className={styles.ctxKey}>{k}</div>
                <div className={styles.ctxVal}>{v}</div>
              </div>
            ))}
            <div className={styles.ctxNote}>You can modify this later if needed.</div>
          </div>
        </div>

        <NavRow onBack={onBack} onNext={onNext} />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
//  SCREEN 3 — Choose Doctor (driven by booking.type)
// ─────────────────────────────────────────────────────────
function Screen3({ onNext, onBack, booking, setBooking }) {
  const cfg = TYPE_CONFIGS[booking.type] || TYPE_CONFIGS.doctor;
  const doctors = cfg.doctors;
  const [sel, setSel] = useState(booking.doctorId || doctors[0].id);

  function handleNext() {
    setBooking((b) => ({ ...b, doctorId: sel }));
    onNext();
  }

  const selDoc = doctors.find((d) => d.id === sel);

  return (
    <>
      <Header step={3} total={7} />
      <div className={styles.body}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 4 }}>
          <div>
            <h2 className={styles.screenTitle}>Choose your doctor</h2>
            <p className={styles.screenSub}>
              Select a specialist for your {cfg.recommendation.title}.
            </p>
          </div>
          <div className={styles.filterRow} style={{ marginBottom: 0 }}>
            <button className={styles.filterBtn}>Filters <ChevDown /></button>
            <button className={styles.filterBtn}>Sort: Relevance <ChevDown /></button>
          </div>
        </div>

        {doctors.map((d) => (
          <div
            key={d.id}
            className={`${styles.docCard} ${sel === d.id ? styles.selected : ""}`}
            onClick={() => setSel(d.id)}
          >
            {/* {d.recommended && (
              <div className={styles.docRecBadge}>Recommended</div>
            )} */}
            
            <div className={styles.docAvatar}>{d.emoji}</div>
            <div className={styles.docInfo}>
              <div className={styles.docName}>{d.name}</div>
              <div className={styles.docTitle}>{d.title}</div>
              <div className={styles.docMeta}>
                <div className={styles.docRating}>
                  <span className={styles.docStar}>★</span>
                  {d.rating} ({d.reviews})
                </div>
                <div className={styles.docExp}>🕐 {d.exp}</div>
              </div>
              <div className={styles.docTags}>
                {d.tags.map((t) => (
                  <span key={t} className={styles.docTag}>{t}</span>
                ))}
              </div>
              <div className={styles.docAvail}>📅 Next available: {d.avail}</div>
            </div>
            <div
              className={styles.radio}
              style={
                sel === d.id
                  ? { background: "var(--plum-d)", borderColor: "var(--plum-d)" }
                  : {}
              }
            >
              {sel === d.id && <div className={styles.radioDot} />}
            </div>
          </div>
        ))}

        <NavRow onBack={onBack} onNext={handleNext} nextLabel="Continue" />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
//  SCREEN 4 — Select Consultation Mode
// ─────────────────────────────────────────────────────────
function Screen4({ onNext, onBack, booking, setBooking }) {
  const [sel, setSel] = useState(booking.mode || CONSULTATION_MODES[0].id);
  const cfg = TYPE_CONFIGS[booking.type] || TYPE_CONFIGS.doctor;
  const rec = cfg.recommendation;
  const modeIlloMap = {
    clinic: "🏥",
    video: "🎥",
    phone: "📞",
  };

  function handleNext() {
    setBooking((b) => ({ ...b, mode: sel }));
    onNext();
  }

  return (
    <>
      <Header step={4} total={7} />
      <div className={styles.body}>
        <div>
          <h2 className={styles.screenTitle}>Select Consultation Mode</h2>
          <p className={styles.screenSub}>
            How would you like to consult? Choose your preferred mode.
          </p>

        </div>
        <div className={styles.twoCol}>
          <div>
            {CONSULTATION_MODES.map((m) => (
              <div
                key={m.id}
                className={`${styles.optionCard} ${sel === m.id ? styles.selected : ""}`}
                onClick={() => setSel(m.id)}
              >
                <div className={styles.optionIcon}>{m.icon}</div>
                <div className={styles.optionText}>
                  <strong>{m.title}</strong>
                  <span>{m.sub}</span>
                </div>
                <Radio checked={sel === m.id} />
              </div>
            ))}
          </div>
          <div className={styles.sidePanel}>
            <div className={styles.sidePanelTitle}>Your Context</div>
            {Object.entries(rec.context).map(([k, v]) => (
              <div key={k} className={styles.ctxRow}>
                <div className={styles.ctxKey}>{k}</div>
                <div className={styles.ctxVal}>{v}</div>
              </div>
            ))}
            <div className={styles.ctxNote}>You can modify this later if needed.</div>
          </div>
        </div>

        <NavRow onBack={onBack} onNext={handleNext} />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
//  Helper: build 7-day date strip
// ─────────────────────────────────────────────────────────
function buildDates() {
  const today = new Date();
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return {
      day: days[d.getDay()],
      date: d.getDate(),
      month: months[d.getMonth()],
      isToday: i === 0,
      fullDate: d,
    };
  });
}

function slotToDate(slot, baseDate) {
  const [time, modifier] = slot.split(" ");
  let [hours, minutes] = time.split(":").map(Number);
  if (modifier === "PM" && hours !== 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;
  const d = new Date(baseDate);
  d.setHours(hours, minutes, 0, 0);
  return d;
}

// ─────────────────────────────────────────────────────────
//  SCREEN 5 — Choose Date & Slot
// ─────────────────────────────────────────────────────────
function Screen5({ onNext, onBack, booking, setBooking }) {
  const dates = useMemo(() => buildDates(), []);
  const [selDateIdx, setSelDateIdx] = useState(0);
  const [selSlot, setSelSlot] = useState(booking.slot || "");

  const disabledSlots = useMemo(() => {
    const disabled = [...MANUALLY_DISABLED_SLOTS];
    if (dates[selDateIdx]?.isToday) {
      const now = new Date();
      ALL_SLOTS.forEach((s) => {
        const t = slotToDate(s, dates[selDateIdx].fullDate);
        if (t <= now && !disabled.includes(s)) disabled.push(s);
      });
    }
    return disabled;
  }, [selDateIdx, dates]);

  const availableSlots = ALL_SLOTS.filter((s) => !disabledSlots.includes(s));
  const nextAvailable = availableSlots[0];

  function handleNext() {
    const d = dates[selDateIdx];
    setBooking((b) => ({
      ...b,
      slot: selSlot,
      date: `${d.isToday ? "Today" : d.day}, ${d.date} ${d.month}`,
    }));
    onNext();
  }

  return (
    <>
      <Header step={5} total={7} />
      <div className={styles.body}>
        <div className={styles.twoColLeft230}>
          {/* LEFT */}
          <div>
            <h2 className={styles.screenTitle}>Choose Date &amp; Slot</h2>
            <p className={styles.screenSub}>Select a date and time that works for you.</p>

            <div className={styles.dateStrip}>
              {dates.map((d, i) => {
                const isSelDate = selDateIdx === i;
                let chipClass = styles.dateChip;
                if (d.isToday) chipClass += " " + styles.dateChipToday;
                else if (isSelDate) chipClass += " " + styles.dateChipSelected;

                return (
                  <div
                    key={i}
                    className={chipClass}
                    onClick={() => {
                      setSelDateIdx(i);
                      setSelSlot("");
                    }}
                  >
                    <span className={styles.dcDay}>{d.isToday ? "Today" : d.day}</span>
                    <span className={styles.dcDate}>{d.date}</span>
                    <span className={styles.dcMonth}>{d.month}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT */}
          <div>
            <div className={styles.slotsHead}>
              <span>
                {dates[selDateIdx].isToday ? "Today" : dates[selDateIdx].day},{" "}
                {dates[selDateIdx].date} {dates[selDateIdx].month}
              </span>
            </div>

            {dates[selDateIdx].isToday && nextAvailable && (
              <div className={styles.nextAvail}>
                <span className={styles.nextAvailDot} />
                Next available today: {nextAvailable}
              </div>
            )}

            {ALL_SLOTS.length === 0 ? (
              <div className={styles.noSlots}>No slots available for this date.</div>
            ) : (
              <div className={styles.slotsGrid}>
                {ALL_SLOTS.map((s) => {
                  const isDisabled = disabledSlots.includes(s);
                  const isSel = selSlot === s;
                  let cls = styles.slotChip;
                  if (isSel) cls += " " + styles.slotChipSelected;
                  else if (isDisabled) cls += " " + styles.slotChipDisabled;

                  return (
                    <button
                      key={s}
                      className={cls}
                      onClick={() => !isDisabled && setSelSlot(s)}
                      disabled={isDisabled}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <NavRow
          onBack={onBack}
          onNext={handleNext}
          disabled={!selSlot}
          nextLabel="Continue"
        />
      </div>
    </>
  );
}


// ─────────────────────────────────────────────────────────
//  SCREEN 6 — Confirm Details
// ─────────────────────────────────────────────────────────
function Screen6({ onNext, onBack, booking, goToStep }) {
  const cfg = TYPE_CONFIGS[booking.type] || TYPE_CONFIGS.doctor;
  const doctor = cfg.doctors.find((d) => d.id === booking.doctorId) || cfg.doctors[0];
  const mode = CONSULTATION_MODES.find((m) => m.id === booking.mode) || CONSULTATION_MODES[0];

  const confirmRows = [
    {
      key: "Service",
      val: cfg.recommendation.title,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      ),
    },
    {
      key: "Date & Time",
      val: `${booking.date} · ${booking.slot}`,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
        </svg>
      ),
    },
    {
      key: "Mode",
      val: mode.title,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 3H8M12 3v4" />
        </svg>
      ),
    },
    {
      key: "Location",
      val: cfg.location,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
          <circle cx="12" cy="9" r="2.5" />
        </svg>
      ),
    },
    {
      key: "Consultation Fee",
      val: `₹${cfg.fee}`,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" />
        </svg>
      ),
    },
  ];

  // Context row icons for the summary panel
  const ctxIcons = {
    Doctor: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
      </svg>
    ),
    default: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
      </svg>
    ),
  };

  return (
    <>
      <Header step={6} total={7} />
      <div className={styles.body}>
        <div className={styles.twoCol}>

          {/* ── LEFT ── */}
          <div>
            {/* Title with check badge */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 6 }}>
              <div style={{
                width: 44, height: 44,
                borderRadius: "50%",
                background: "var(--plum-d)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className={styles.screenTitle} style={{ marginBottom: 0 }}>Confirm Details</h2>
            </div>
            <p className={styles.screenSub}>
              Please review your appointment details before proceeding to payment.
            </p>

            {/* Doctor card */}
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              padding: "16px 20px",
              border: "1.5px solid var(--primary-color)",
              borderRadius: "var(--radius)",
              marginBottom: 0,
              background: "var(--card-bg)",
            }}>
              <div style={{
                width: 56, height: 56,
                borderRadius: "50%",
                background: "var(--plum-d)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 26,
                flexShrink: 0,
                border: "2px solid var(--plum-b)",
              }}>
                {doctor.emoji}
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "var(--text)", lineHeight: 1.2 }}>
                  {doctor.name}
                </div>
                <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 3 }}>
                  {doctor.title}
                </div>
              </div>
            </div>

            {/* Confirm rows with icons */}
            <div style={{
              border: "1.5px solid var(--primary-color)",
              borderRadius: "var(--radius)",
              overflow: "hidden",
              marginTop: 12,
            }}>
              {confirmRows.map((r, i) => (
                <div
                  key={r.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "14px 20px",
                    borderBottom: i < confirmRows.length - 1 ? "1px solid var(--primary-color)" : "none",
                    background: "var(--card-bg)",
                  }}
                >
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    color: "var(--muted)",
                  }}>
                    <div style={{
                      width: 32, height: 32,
                      borderRadius: "var(--radius-sm)",
                      background: "var(--plum-s)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                      color: "var(--plum-m)",
                    }}>
                      {r.icon}
                    </div>
                    <span style={{ fontSize: 13, color: "var(--muted)", fontWeight: 500 }}>{r.key}</span>
                  </div>
                  <span style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: r.key === "Consultation Fee" ? "var(--plum-d)" : "var(--text)",
                    fontSize: r.key === "Consultation Fee" ? 16 : 14,
                    textAlign: "right",
                    maxWidth: "55%",
                  }}>
                    {r.val}
                  </span>
                </div>
              ))}
            </div>

            {/* Edit Details — centered */}
            <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
              <button
                onClick={() => goToStep(0)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  background: "transparent",
                  border: "1.5px solid var(--plum-b)",
                  color: "var(--plum-m)",
                  fontSize: 13,
                  fontWeight: 700,
                  padding: "9px 22px",
                  borderRadius: "var(--radius-sm)",
                  cursor: "pointer",
                  fontFamily: "var(--font)",
                  transition: "var(--trans)",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = "var(--plum-m)";
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "var(--plum-m)";
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit Details
              </button>
            </div>
          </div>

          {/* ── RIGHT — Appointment Summary ── */}
          <div style={{
            background: "var(--plum-l)",
            border: "1.5px solid var(--plum-b)",
            borderRadius: "var(--radius-lg)",
            padding: "20px",
            position: "relative",
            overflow: "hidden",
          }}>
            {/* Header with illustration */}
            <div style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              marginBottom: 18,
            }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "var(--plum-d)", letterSpacing: 0.3 }}>
                Appointment Summary
              </div>
              <div style={{
                width: 44, height: 44,
                borderRadius: "50%",
                background: "var(--plum-s)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 22,
                border: "1.5px solid var(--plum-b)",
              }}>
                📋
              </div>
            </div>

            {/* Summary rows */}
            {[
              { key: "DOCTOR", val: doctor.name },
              ...Object.entries(cfg.recommendation.context).map(([k, v]) => ({
                key: k.toUpperCase(),
                val: v,
              })),
            ].map((r, i, arr) => (
              <div
                key={r.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  padding: "10px 0",
                  borderBottom: i < arr.length - 1 ? "1px solid var(--plum-b)" : "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{
                    width: 26, height: 26,
                    borderRadius: "50%",
                    background: "var(--plum-s)",
                    border: "1px solid var(--plum-b)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                    color: "var(--plum-m)",
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <span style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--plum-m)",
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                  }}>
                    {r.key}
                  </span>
                </div>
                <span style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--text)",
                  textAlign: "right",
                  maxWidth: "55%",
                }}>
                  {r.val}
                </span>
              </div>
            ))}

            {/* Fee row */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 14,
              paddingTop: 14,
              borderTop: "1.5px solid var(--plum-b)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  width: 26, height: 26,
                  borderRadius: "50%",
                  background: "var(--plum-s)",
                  border: "1px solid var(--plum-b)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                  color: "var(--plum-m)",
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" />
                  </svg>
                </div>
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "var(--plum-m)",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                }}>
                  FEE
                </span>
              </div>
              <span style={{
                fontSize: 20,
                fontWeight: 800,
                color: "var(--plum-d)",
                letterSpacing: -0.5,
              }}>
                ₹{cfg.fee}
              </span>
            </div>
          </div>
        </div>

        <NavRow onBack={onBack} onNext={onNext} nextLabel="Continue to Payment" />
      </div>
    </>
  );
}


// ─────────────────────────────────────────────────────────
//  SCREEN 7 — Payment
// ─────────────────────────────────────────────────────────
function Screen7({ onNext, onBack, booking }) {
  const [sel, setSel] = useState("upi");
  const cfg = TYPE_CONFIGS[booking.type] || TYPE_CONFIGS.doctor;
  const doctor =
    (TYPE_CONFIGS[booking.type]?.doctors || []).find(
      (d) => d.id === booking.doctorId
    ) || TYPE_CONFIGS[booking.type]?.doctors?.[0];

  const METHODS = [
    {
      id: "upi",
      label: "UPI",
      sub: "Google Pay, PhonePe, Paytm, BHIM",
      icon: (
        <span style={{ fontSize: 16, fontWeight: 900, letterSpacing: -1, color: "var(--plum-d)", lineHeight: 1 }}>
          U<span style={{ color: "var(--plum-m)" }}>P</span>I
        </span>
      ),
    },
    {
      id: "card",
      label: "Debit / Credit Card",
      sub: "Visa, Mastercard, RuPay",
      icon: (
        <svg width="32" height="22" viewBox="0 0 36 24" fill="none">
          <rect width="36" height="24" rx="4" fill="var(--plum-l)" />
          <rect x="3" y="9" width="30" height="4" fill="var(--plum-d)" rx="1" />
          <rect x="3" y="16" width="8" height="3" rx="1" fill="var(--plum-b)" />
          <rect x="14" y="16" width="5" height="3" rx="1" fill="var(--plum-b)" />
        </svg>
      ),
    },
    {
      id: "netbanking",
      label: "Net Banking",
      sub: "All major banks supported",
      icon: (
        <svg width="28" height="26" viewBox="0 0 32 28" fill="none">
          <rect x="2" y="10" width="28" height="16" rx="2" fill="var(--plum-l)" stroke="var(--plum-b)" strokeWidth="1.5" />
          <rect x="6" y="14" width="4" height="8" rx="1" fill="var(--plum-d)" />
          <rect x="14" y="14" width="4" height="8" rx="1" fill="var(--plum-d)" />
          <rect x="22" y="14" width="4" height="8" rx="1" fill="var(--plum-d)" />
          <path d="M16 2L2 10h28L16 2z" fill="var(--plum-d)" />
        </svg>
      ),
    },
    {
      id: "wallet",
      label: "Wallets",
      sub: "Paytm, Mobikwik, Amazon Pay",
      icon: (
        <svg width="28" height="26" viewBox="0 0 32 28" fill="none">
          <rect x="2" y="6" width="28" height="18" rx="3" fill="var(--plum-l)" stroke="var(--plum-b)" strokeWidth="1.5" />
          <rect x="20" y="10" width="8" height="8" rx="3" fill="var(--plum-d)" />
          <circle cx="23" cy="14" r="2" fill="var(--plum-l)" />
        </svg>
      ),
    },
  ];

  return (
    <>
      <Header step={7} total={7} />

      <div className={styles.body}>
        <div className={styles.twoCol} style={{ gridTemplateColumns: "1fr 280px" }}>

          {/* ── LEFT ── */}
          <div>
            {/* Page header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div className={styles.optionIcon} style={{ background: "var(--plum-s)", flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--plum-m)" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
              </div>
              <div>
                <h2 className={styles.screenTitle} style={{ marginBottom: 2 }}>Complete Your Payment</h2>
                <p className={styles.screenSub} style={{ marginBottom: 0 }}>
                  Secure your appointment by completing the payment.
                </p>
              </div>
            </div>

            {/* Amount card */}
            <div style={{
              background: "var(--plum-d)",
              borderRadius: "var(--radius)",
              padding: "18px 22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 22,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{
                  width: 42, height: 42,
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "var(--radius-sm)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <rect x="2" y="5" width="20" height="14" rx="2" />
                    <path d="M2 10h20" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Amount Payable</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", marginTop: 2 }}>Consultation Fee</div>
                </div>
              </div>
              <div style={{ fontSize: 30, fontWeight: 800, color: "#fff", letterSpacing: -1 }}>₹{cfg.fee}</div>
            </div>

            {/* Payment methods label */}
            <div className={styles.payMethodsLabel}>Choose a payment method</div>

            {/* Payment methods */}
            {METHODS.map((m) => (
              <div
                key={m.id}
                className={`${styles.payMethod} ${sel === m.id ? styles.selected : ""}`}
                onClick={() => setSel(m.id)}
              >
                <div className={styles.payMethodIcon}>{m.icon}</div>
                <div className={styles.payMethodText} style={{ flex: 1 }}>
                  <strong>
                    {m.label}
                    <span style={{
                      display: "inline-block",
                      background: "var(--green-l)",
                      color: "var(--green)",
                      border: "1px solid var(--green-b)",
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: 99,
                      marginLeft: 8,
                      verticalAlign: "middle",
                    }}>
                      Instant &amp; Secure
                    </span>
                  </strong>
                  <span>{m.sub}</span>
                </div>
                <Radio checked={sel === m.id} />
              </div>
            ))}

            {/* Trust row */}
            {/* <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              border: "1px solid var(--primary-color)",
              borderRadius: "var(--radius)",
              overflow: "hidden",
              marginTop: 18,
            }}>
              {[
                {
                  bg: "#eff6ff", stroke: "#2563eb",
                  path: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
                  title: "100% Secure", sub: "Your data is protected",
                },
                {
                  bg: "var(--plum-l)", stroke: "var(--plum-m)",
                  path: <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />,
                  title: "Instant Confirmation", sub: "Confirmed immediately",
                },
                {
                  bg: "#f0fdfa", stroke: "#0d9488",
                  path: <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />,
                  title: "Need Help?", sub: "Contact our support",
                },
              ].map((t, i) => (
                <div key={i} style={{
                  padding: "12px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  borderRight: i < 2 ? "1px solid var(--primary-color)" : "none",
                  background: "var(--card-bg)",
                }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: "var(--radius-sm)",
                    background: t.bg,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={t.stroke} strokeWidth="2">
                      {t.path}
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text)" }}>{t.title}</div>
                    <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 1 }}>{t.sub}</div>
                  </div>
                </div>
              ))}
            </div> */}
          </div>

          {/* ── RIGHT — Appointment Summary ── */}
          <div className={styles.payIllo} style={{ padding: 20 }}>
            <div className={styles.sidePanelTitle}>Appointment Summary</div>

            {/* Date row */}
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 0",
              borderBottom: "1px solid var(--primary-color)",
            }}>
              <div style={{
                width: 28, height: 28,
                borderRadius: "var(--radius-sm)",
                background: "var(--plum-s)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--plum-m)" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
              </div>
              <span style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--text)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}>
                {booking.date || "—"}
              </span>
            </div>

            {/* Time row */}
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 0",
              borderBottom: "1px solid var(--primary-color)",
            }}>
              <div style={{
                width: 28, height: 28,
                borderRadius: "var(--radius-sm)",
                background: "var(--plum-s)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--plum-m)" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <span style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--text)",
                whiteSpace: "nowrap",
              }}>
                {booking.slot || "—"}
              </span>
            </div>

            {/* Doctor */}
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 0",
              borderBottom: "1px solid var(--primary-color)",
              marginBottom: 4,
            }}>
              <div className={styles.confirmDocAvatar}>{doctor?.emoji || "👩‍⚕️"}</div>
              <div>
                <div className={styles.confirmDocName}>{doctor?.name || "—"}</div>
                <div className={styles.confirmDocTitle}>{doctor?.title || "—"}</div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1 }}>{cfg.location || "—"}</div>
              </div>
            </div>

            {/* Payment details */}
            <hr className={styles.summaryDivider} />
            <div className={styles.sidePanelTitle} style={{ marginBottom: 10 }}>Payment Details</div>

            <div className={styles.summaryRow}>
              <span className={styles.summaryKey}>Consultation Fee</span>
              <span className={styles.summaryVal}>₹{cfg.fee}</span>
            </div>
            <div className={styles.summaryRow} style={{
              borderTop: "1px solid var(--plum-b)",
              paddingTop: 8,
              marginTop: 4,
            }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: "var(--plum-d)" }}>Total Amount</span>
              <span style={{ fontSize: 15, fontWeight: 800, color: "var(--plum-d)" }}>₹{cfg.fee}</span>
            </div>

            {/* Security items */}
            <hr className={styles.summaryDivider} />
            {[
              {
                bg: "var(--green-l)", stroke: "var(--green)",
                path: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
                title: "Secure Payment",
                sub: "Your transaction is encrypted and 100% secure",
              },
              {
                bg: "#f0fdfa", stroke: "#0d9488",
                path: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></>,
                title: "PCI DSS Compliant",
                sub: "Industry-standard security protocols",
              },
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                <div style={{
                  width: 32, height: 32,
                  borderRadius: "var(--radius-sm)",
                  background: s.bg, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={s.stroke} strokeWidth="2">{s.path}</svg>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>{s.title}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 1 }}>{s.sub}</div>
                </div>
              </div>
            ))}

            {/* Brand logos */}
            <div className={styles.payBadges} style={{ justifyContent: "space-between", marginTop: 14 }}>
              <span style={{ background: "#1e40af", color: "#fff", fontSize: 10, fontWeight: 800, padding: "4px 8px", borderRadius: 6 }}>
                PCI DSS
              </span>
              <span style={{ background: "#ff6b00", color: "#fff", fontSize: 10, fontWeight: 800, padding: "4px 8px", borderRadius: 6 }}>
                Ru<span style={{ color: "#ffd700" }}>Pay</span>
              </span>
              <span style={{ color: "#072654", fontWeight: 800, fontSize: 11 }}>/Razorpay</span>
            </div>
          </div>
        </div>

        {/* ── FOOTER NAV ── */}
        <div className={`${styles.navRow} ${styles.navRowSpaced}`}>
          <button className={styles.btnOutline} onClick={onBack}>
            <ArrowLeft /> Back
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--muted)" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
            You will be redirected to the payment gateway to complete your transaction securely.
          </div>
          <button className={styles.btnPrimary} onClick={onNext} style={{ padding: "12px 28px", fontSize: 14 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
            Pay ₹{cfg.fee} Securely <ArrowRight />
          </button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
//  SCREEN 8 — Appointment Confirmed
// ─────────────────────────────────────────────────────────
function Screen8({ booking }) {
  const cfg = TYPE_CONFIGS[booking.type] || TYPE_CONFIGS.doctor;
  const doctor = cfg.doctors.find((d) => d.id === booking.doctorId) || cfg.doctors[0];
  const mode = CONSULTATION_MODES.find((m) => m.id === booking.mode) || CONSULTATION_MODES[0];

  const aptId = `APT-${Math.floor(10000000 + Math.random() * 90000000)}`;

  const aptRows = [
    { key: "Doctor", val: doctor.name },
    { key: "Date & Time", val: `${booking.date} · ${booking.slot}` },
    { key: "Mode", val: mode.title },
    { key: "Location", val: cfg.location },
  ];

  return (
    <>
      <Header step={7} total={7} />
      <div className={styles.body}>
        <div className={styles.confirmCenter}>
          <div className={styles.confirmCheck}>✓</div>
          <h2 className={styles.confirmHeadline}>Appointment Confirmed!</h2>
          <p className={styles.confirmDesc}>
            Your appointment has been booked successfully.
          </p>

          <div className={styles.confirmAptCard}>
            <div className={styles.confirmAptId}>📋 {aptId}</div>
            {aptRows.map((r) => (
              <div key={r.key} className={styles.confirmAptRow}>
                <span className={styles.carKey}>{r.key}</span>
                <span className={styles.carVal}>{r.val}</span>
              </div>
            ))}
          </div>

          <div className={styles.confirmSms}>
            ✅ A confirmation email &amp; SMS has been sent to you.
          </div>

          <div className={styles.confirmActions}>
            <a href="/" className={styles.btnPrimary}>
              Go to Dashboard
            </a>
            <button className={styles.btnSecondary}>📅 Add to Calendar</button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
//  ORCHESTRATOR
// ─────────────────────────────────────────────────────────
const SCREEN_COUNT = 7; // steps shown in progress bar (step 8 is confirmation, same as 7)

export default function BookingFlow() {
  const [step, setStep] = useState(0);
  const [booking, setBooking] = useState({
    type: APPOINTMENT_TYPES[0].id,
    doctorId: null,
    mode: null,
    date: null,
    slot: null,
  });

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));
  const goToStep = (s) => setStep(s);

  const sharedProps = { booking, setBooking, onNext: next, onBack: back, goToStep };

  const screens = [
    <Screen1 key="s1" {...sharedProps} />,
    <Screen2 key="s2" {...sharedProps} />,
    <Screen3 key="s3" {...sharedProps} />,
    <Screen4 key="s4" {...sharedProps} />,
    <Screen5 key="s5" {...sharedProps} />,
    <Screen6 key="s6" {...sharedProps} />,
    <Screen7 key="s7" {...sharedProps} />,
    <Screen8 key="s8" {...sharedProps} />,
  ];

  return (
    <>
    <DigitalHealthFAB/>
    <div className={styles.page}>
      <div className={styles.card}>
        {screens[step]}
      </div>
    </div>
    </>
  );
}